import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ContentLayout, StepperCard } from '@ibk/shared-layout';
import { useTranslation } from '@ibk/shared-translation';
import { ReleaseType } from '@ibk/shared-types';
import { Alert, Icon } from '@ibk/shared-ui';
import { formatCurrency } from '@ibk/shared-utils';
import { AnchoredAlert } from '../../../../components/AnchoredAlert';
import { BeneficiaryValuesForm } from '../../../../components/BeneficiaryValuesForm';
import { useEmissorBeneficiaryFilters, useEmissorBusinessDays, useEmissorContractBeneficiaries, useEmissorUpdateBeneficiary, } from '../../../../hooks';
import { BeneficiaryStatusInProduct, } from '../../../../types/voucher-beneficiaries';
import { getBeneficiaryAnalysisStatus, getUnformatedValue, hasAnyFilter, isActiveStatus, } from '../../../../utils/beneficiaryUtils';
import { NewRechargeStep, NewRechargeStepStatus, SimulationState, } from '../../types';
import styles from '../BeneficiariesValues/BeneficiariesValues.module.css';
import { applyGlobalValuesToBeneficiaries } from '../BeneficiariesValues/utils/beneficiariesValuesUtils';
import { EmissorSpreadsheetImportModal } from '../EmissorSpreadsheetImportModal/EmissorSpreadsheetImportModal';
import { normalizeCpf } from '../EmissorSpreadsheetImportModal/utils/outcomeMapper';
import { EmissorBeneficiaryEditModal, } from './components/EmissorBeneficiaryEditModal';
import { mapEmissorBeneficiariesToVoucherBeneficiaries, mapFilterDataToEmissorSearchBody, } from './emissorBeneficiariesMapper';
const DEFAULT_CURRENCY_VALUE = 'R$ 0,00';
function toReleaseType(value) {
    return value === ReleaseType.DAILY ? ReleaseType.DAILY : ReleaseType.MONTHLY;
}
function mapEmissorFilterOptions(options) {
    if (!options) {
        return undefined;
    }
    return {
        jobRole: options.jobRole,
        costCenter: options.costCenter,
        department: options.department,
        category: options.category,
        employmentBond: options.employmentBond,
        branch: options.branch,
        union: options.union,
    };
}
export function EmissorBeneficiariesValues({ contractId, selectedProduct, simulationState, formData, onUpdateBeneficiariesData, onComplete, onReturn, isBeneficiaryDiscountEnabled, headerStepper, }) {
    const { t } = useTranslation();
    const [showInfoAlert, setShowInfoAlert] = useState(true);
    const [showPendingAlert, setShowPendingAlert] = useState(true);
    const [showZeroValueError, setShowZeroValueError] = useState(false);
    const [showImportModal, setShowImportModal] = useState(false);
    const [appliedFilters, setAppliedFilters] = useState(formData.beneficiaryFilters ?? null);
    const productId = formData.voucherTypeId ?? null;
    const [releaseType, setReleaseType] = useState(() => toReleaseType(formData.releaseType ?? selectedProduct?.releaseType));
    const defaultMonthlyValue = selectedProduct?.baseValue
        ? formatCurrency(selectedProduct.baseValue)
        : DEFAULT_CURRENCY_VALUE;
    const defaultDailyValue = selectedProduct?.amountDaily
        ? formatCurrency(selectedProduct.amountDaily)
        : DEFAULT_CURRENCY_VALUE;
    const [beneficiaryHistory, setBeneficiaryHistory] = useState(() => ({
        monthly: {
            globalValue: formData.releaseType === ReleaseType.MONTHLY
                ? (formData.globalValue ?? defaultMonthlyValue)
                : defaultMonthlyValue,
        },
        daily: {
            globalValue: formData.releaseType === ReleaseType.DAILY ? (formData.globalValue ?? defaultDailyValue) : defaultDailyValue,
        },
    }));
    const globalValue = beneficiaryHistory[releaseType === ReleaseType.MONTHLY ? 'monthly' : 'daily'].globalValue;
    const competenceString = formData.competenceDate
        ? `${formData.competenceDate.getFullYear()}-${String(formData.competenceDate.getMonth() + 1).padStart(2, '0')}`
        : undefined;
    const { businessDays: businessDaysData } = useEmissorBusinessDays({
        competence: competenceString,
        skip: releaseType !== ReleaseType.DAILY,
    });
    const [businessDays, setBusinessDays] = useState(formData.businessDays ?? 0);
    useEffect(() => {
        if (releaseType === ReleaseType.DAILY && businessDaysData > 0 && !formData.businessDays) {
            setBusinessDays(businessDaysData);
        }
    }, [releaseType, businessDaysData, formData.businessDays]);
    const searchBody = useMemo(() => mapFilterDataToEmissorSearchBody(appliedFilters), [appliedFilters]);
    const { beneficiaries: rawBeneficiaries, total: totalBeneficiaries, isLoading: isLoadingBeneficiaries, isError, refetch: refetchBeneficiaries, } = useEmissorContractBeneficiaries({
        contractId,
        productId,
        searchBody,
    });
    const { updateBeneficiary, isUpdating } = useEmissorUpdateBeneficiary();
    const [editingBeneficiary, setEditingBeneficiary] = useState(null);
    const [showUpdateSuccess, setShowUpdateSuccess] = useState(false);
    const [showUpdateError, setShowUpdateError] = useState(false);
    const { filterOptions: emissorFilterOptions, isLoading: isLoadingFilters } = useEmissorBeneficiaryFilters({
        contractId,
    });
    const filterOptions = useMemo(() => mapEmissorFilterOptions(emissorFilterOptions), [emissorFilterOptions]);
    const [beneficiaries, setBeneficiaries] = useState(formData.beneficiaries ?? []);
    const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
    const selectedBeneficiariesListRef = useRef(formData.selectedBeneficiariesList ?? []);
    const hasInitializedRef = useRef(false);
    const rawBeneficiariesKey = useMemo(() => rawBeneficiaries
        .map(beneficiary => `${beneficiary.id}:${beneficiary.kycStatus ?? ''}:${beneficiary.identityStatus ?? ''}:${beneficiary.firstName}:${beneficiary.lastName}:${beneficiary.birthDate ?? ''}`)
        .join('|'), [rawBeneficiaries]);
    useEffect(() => {
        if (!hasInitializedRef.current && !rawBeneficiaries.length) {
            return;
        }
        const savedMap = new Map(selectedBeneficiariesListRef.current.map(selected => [selected.beneficiaryId, selected]));
        let mapped = mapEmissorBeneficiariesToVoucherBeneficiaries(rawBeneficiaries);
        if (!hasInitializedRef.current) {
            mapped = applyGlobalValuesToBeneficiaries(mapped, globalValue, releaseType, businessDays);
            hasInitializedRef.current = true;
            setHasLoadedOnce(true);
        }
        mapped = mapped.map(beneficiary => {
            const saved = savedMap.get(beneficiary.id);
            if (saved) {
                return {
                    ...beneficiary,
                    statusInProduct: BeneficiaryStatusInProduct.ACTIVE,
                    productValue: saved.value,
                    productDaysQuantity: saved.daysQuantity ?? 0,
                    productDiscountValue: saved.discountValue ?? 0,
                };
            }
            return beneficiary;
        });
        setBeneficiaries(mapped);
    }, [rawBeneficiariesKey]);
    const selectedBeneficiariesList = useMemo(() => {
        const merged = new Map(selectedBeneficiariesListRef.current.map(selected => [selected.beneficiaryId, selected]));
        beneficiaries.forEach(beneficiary => {
            if (isActiveStatus(beneficiary.statusInProduct)) {
                merged.set(beneficiary.id, {
                    beneficiaryId: beneficiary.id,
                    value: beneficiary.productValue,
                    daysQuantity: beneficiary.productDaysQuantity ?? 0,
                    discountValue: beneficiary.productDiscountValue ?? 0,
                });
            }
            else if (merged.has(beneficiary.id)) {
                merged.delete(beneficiary.id);
            }
        });
        return Array.from(merged.values());
    }, [beneficiaries]);
    useEffect(() => {
        selectedBeneficiariesListRef.current = selectedBeneficiariesList;
        onUpdateBeneficiariesData({
            beneficiaries,
            globalValue,
            businessDays,
            releaseType,
            selectedBeneficiaries: selectedBeneficiariesList.length,
            selectedBeneficiariesList,
            amountDaily: releaseType === ReleaseType.DAILY ? getUnformatedValue(globalValue) : undefined,
        });
    }, [beneficiaries, selectedBeneficiariesList, globalValue, businessDays, releaseType, onUpdateBeneficiariesData]);
    const breadcrumbs = useMemo(() => [
        { label: t('recharge.breadcrumb.benefits'), href: '/beneficios' },
        { label: t('recharge.breadcrumb.new-recharge'), isActive: true },
    ], [t]);
    const currentStep = useMemo(() => ({
        id: NewRechargeStep.BENEFICIARIES_VALUES,
        title: t('recharge.step.beneficiaries-values'),
        number: 3,
        status: NewRechargeStepStatus.ACTIVE,
    }), [t]);
    const handleGlobalValueChange = useCallback((value) => {
        const type = releaseType === ReleaseType.MONTHLY ? 'monthly' : 'daily';
        setBeneficiaryHistory(prev => ({ ...prev, [type]: { globalValue: value } }));
    }, [releaseType]);
    const handleApplyValue = useCallback(() => {
        setBeneficiaries(prev => applyGlobalValuesToBeneficiaries(prev, globalValue, releaseType, businessDays));
    }, [globalValue, releaseType, businessDays]);
    const handleBusinessDaysChange = useCallback((days) => {
        setBusinessDays(days);
    }, []);
    const handleReleaseTypeChange = useCallback((value) => {
        setReleaseType(toReleaseType(value));
    }, []);
    const handleUpdateBeneficiaries = useCallback((updated) => {
        setBeneficiaries(updated);
    }, []);
    const handleFilterChange = useCallback((filters) => {
        const hasActiveFilters = hasAnyFilter(filters, true);
        const filtersToApply = hasActiveFilters ? filters : null;
        setAppliedFilters(filtersToApply);
        onUpdateBeneficiariesData({ beneficiaryFilters: filtersToApply });
    }, [onUpdateBeneficiariesData]);
    const handleCloseInfoAlert = useCallback(() => setShowInfoAlert(false), []);
    const handleCorrectBeneficiary = useCallback((id) => {
        const beneficiary = beneficiaries.find(item => item.id === id) ?? null;
        setShowUpdateError(false);
        setEditingBeneficiary(beneficiary);
    }, [beneficiaries]);
    const handleCloseEditModal = useCallback(() => {
        setShowUpdateError(false);
        setEditingBeneficiary(null);
    }, []);
    const editModalVariant = useMemo(() => {
        if (!editingBeneficiary) {
            return 'edit';
        }
        const status = getBeneficiaryAnalysisStatus(editingBeneficiary);
        if (status === 'REJECTED') {
            return 'edit';
        }
        if (status === 'PENDING') {
            return 'pending';
        }
        return 'view';
    }, [editingBeneficiary]);
    const handleSaveBeneficiary = useCallback(async (beneficiaryId, body) => {
        if (!contractId) {
            return;
        }
        setShowUpdateError(false);
        try {
            await updateBeneficiary({ contractId, beneficiaryId, body });
            setEditingBeneficiary(null);
            setShowUpdateSuccess(true);
            refetchBeneficiaries();
        }
        catch {
            setShowUpdateError(true);
        }
    }, [contractId, updateBeneficiary, refetchBeneficiaries]);
    const totalFromSelectedList = selectedBeneficiariesList.reduce((total, selected) => total + (selected.value ?? 0), 0);
    const hasMonthlyZeroValue = releaseType === ReleaseType.MONTHLY &&
        selectedBeneficiariesList.length > 0 &&
        selectedBeneficiariesList.some(selected => !selected.value || selected.value === 0);
    const hasDailyZeroValue = releaseType === ReleaseType.DAILY &&
        selectedBeneficiariesList.length > 0 &&
        selectedBeneficiariesList.some(selected => !selected.daysQuantity || selected.daysQuantity === 0);
    useEffect(() => {
        if (!hasMonthlyZeroValue && !hasDailyZeroValue) {
            setShowZeroValueError(false);
        }
    }, [hasMonthlyZeroValue, hasDailyZeroValue]);
    const isFormValid = selectedBeneficiariesList.length > 0 &&
        (releaseType !== ReleaseType.DAILY ||
            (getUnformatedValue(globalValue) > 0 &&
                selectedBeneficiariesList.some(selected => selected.daysQuantity && selected.daysQuantity > 0)));
    const isSimulating = simulationState === SimulationState.SIMULATING;
    const hasPendingRegistrations = useMemo(() => beneficiaries.some(beneficiary => {
        const analysisStatus = getBeneficiaryAnalysisStatus(beneficiary);
        return analysisStatus !== null && analysisStatus !== 'APPROVED';
    }), [beneficiaries]);
    const handleNext = useCallback(() => {
        if (hasMonthlyZeroValue || hasDailyZeroValue) {
            setShowZeroValueError(true);
            return;
        }
        onComplete(true);
    }, [hasMonthlyZeroValue, hasDailyZeroValue, onComplete]);
    const handleOpenImportModal = useCallback(() => {
        setShowImportModal(true);
    }, []);
    const handleCloseImportModal = useCallback(() => {
        setShowImportModal(false);
    }, []);
    const handleManualEntry = useCallback(() => {
        setShowImportModal(false);
    }, []);
    const handleImportComplete = useCallback((validRows) => {
        const globalNumericValue = getUnformatedValue(globalValue);
        const isDaily = releaseType === ReleaseType.DAILY;
        const isMonthly = releaseType === ReleaseType.MONTHLY;
        const isDiscountColumnRendered = isBeneficiaryDiscountEnabled && (formData.payrollDeduction ?? false);
        const computeImportedProductValue = (row) => {
            if (!isDaily) {
                return row.value;
            }
            if (row.daysQuantity === 0) {
                return row.value;
            }
            return globalNumericValue * row.daysQuantity;
        };
        const buildImportedFields = () => ({
            value: isMonthly,
            days: isDaily,
            discount: isDiscountColumnRendered,
        });
        const updatedBeneficiaries = beneficiaries.map(beneficiary => {
            const normalizedDoc = normalizeCpf(beneficiary.document);
            const importedRow = validRows.find(row => row.normalizedCpf === normalizedDoc);
            if (importedRow) {
                return {
                    ...beneficiary,
                    statusInProduct: BeneficiaryStatusInProduct.ACTIVE,
                    productValue: computeImportedProductValue(importedRow),
                    productDaysQuantity: importedRow.daysQuantity,
                    productDiscountValue: importedRow.discountValue,
                    importedFields: buildImportedFields(),
                };
            }
            return {
                ...beneficiary,
                statusInProduct: BeneficiaryStatusInProduct.INACTIVE,
            };
        });
        setBeneficiaries(updatedBeneficiaries);
        const selectedList = validRows.reduce((acc, row) => {
            const beneficiary = updatedBeneficiaries.find(b => normalizeCpf(b.document) === row.normalizedCpf);
            if (beneficiary) {
                acc.push({
                    beneficiaryId: beneficiary.id,
                    value: computeImportedProductValue(row),
                    daysQuantity: row.daysQuantity,
                    discountValue: row.discountValue,
                });
            }
            return acc;
        }, []);
        selectedBeneficiariesListRef.current = selectedList;
        onUpdateBeneficiariesData({
            beneficiaries: updatedBeneficiaries,
            selectedBeneficiariesList: selectedList,
            selectedBeneficiaries: selectedList.length,
        });
        setShowImportModal(false);
    }, [
        beneficiaries,
        formData.payrollDeduction,
        globalValue,
        isBeneficiaryDiscountEnabled,
        onUpdateBeneficiariesData,
        releaseType,
    ]);
    const importSpreadsheetAction = (_jsxs("button", { type: 'button', className: styles.importButton, onClick: handleOpenImportModal, disabled: isLoadingBeneficiaries, "data-testid": 'buttonNewRechargeEmissorBeneficiariesValuesImportSpreadsheet', children: [_jsx(Icon, { name: 'uploadOrange', alt: '', className: styles.importButtonIcon }), t('recharge.step.beneficiaries-values.import-button')] }));
    const renderMainCard = () => (_jsxs(StepperCard, { currentStep: currentStep, showStepper: !headerStepper, totalValue: formatCurrency(totalFromSelectedList), actions: {
            back: {
                label: t('recharge.button.back'),
                onClick: onReturn,
            },
            next: {
                labelLoading: t('recharge.step.beneficiaries-values.button.loading'),
                label: t('recharge.button.next'),
                onClick: handleNext,
                loading: isSimulating,
                disabled: !isFormValid || isSimulating || !contractId,
            },
        }, children: [headerStepper, _jsxs("div", { className: styles.stepContentEmissor, "data-testid": 'viewNewRechargeEmissorBeneficiariesValuesStepContent', children: [hasPendingRegistrations && showPendingAlert && (_jsx(Alert, { message: t('recharge.beneficiaries.analysis-status.pending-alert'), appearance: 'warning', alertType: 'inline', alignItems: 'center', className: styles.pendingAlert, onClose: () => setShowPendingAlert(false), "data-testid": 'alertNewRechargeEmissorBeneficiariesPending' })), _jsx(BeneficiaryValuesForm, { context: 'recharge', releaseType: releaseType, globalValue: globalValue, beneficiaries: beneficiaries, businessDays: businessDays, showAlert: showInfoAlert, isLoading: isLoadingBeneficiaries && !hasLoadedOnce, isFiltering: isLoadingBeneficiaries && hasLoadedOnce, appliedFilters: appliedFilters, isBeneficiaryDiscountEnabled: isBeneficiaryDiscountEnabled, payrollDeduction: formData.payrollDeduction ?? false, discountType: formData.discountType, discountValue: formData.discountValue, onReleaseTypeChange: handleReleaseTypeChange, onGlobalValueChange: handleGlobalValueChange, onUpdateBeneficiaries: handleUpdateBeneficiaries, onApplyValue: handleApplyValue, onBusinessDaysChange: handleBusinessDaysChange, onCloseAlert: handleCloseInfoAlert, onFilterChange: handleFilterChange, filterOptions: filterOptions, isLoadingFilters: isLoadingFilters, hasZeroValueError: showZeroValueError, showAnalysisStatus: true, infoAsPlainText: true, hideTypeColumn: true, showActionColumn: true, onCorrectBeneficiary: handleCorrectBeneficiary, beneficiariesLabel: t('recharge.beneficiaries.column.collaborators', { count: totalBeneficiaries }), valueColumnLabel: t('recharge.beneficiaries.column.total'), filterActions: importSpreadsheetAction }), isError && (_jsx(AnchoredAlert, { message: t('recharge.step.beneficiaries-values.error.message'), appearance: 'error', duration: 8 })), showZeroValueError && (_jsx(AnchoredAlert, { message: t('recharge.validation.zero-value-beneficiaries'), appearance: 'error', duration: 5, onClose: () => setShowZeroValueError(false) })), showUpdateSuccess && (_jsx(AnchoredAlert, { message: t('recharge.beneficiaries.edit-modal.success'), appearance: 'success', duration: 5, onClose: () => setShowUpdateSuccess(false) }))] })] }));
    return (_jsxs("div", { className: styles.pageWrapper, children: [_jsx(ContentLayout, { breadcrumbs: breadcrumbs, showSidebar: false, noMaxWidth: true, className: styles.fullHeightContentLayout, children: renderMainCard() }), _jsx(EmissorBeneficiaryEditModal, { show: editingBeneficiary !== null, beneficiary: editingBeneficiary, isSaving: isUpdating, variant: editModalVariant, errorMessage: showUpdateError ? t('recharge.beneficiaries.edit-modal.error') : undefined, onClose: handleCloseEditModal, onSave: handleSaveBeneficiary, onDismissError: () => setShowUpdateError(false) }), _jsx(EmissorSpreadsheetImportModal, { isOpen: showImportModal, onClose: handleCloseImportModal, onManualEntry: handleManualEntry, onImportComplete: handleImportComplete, contractId: contractId ?? '', productId: productId ?? '', releaseType: releaseType, discountType: formData.discountType })] }));
}
